import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { LOGO_URL, DOC_TYPES } from '../data/mock';
import {
  ScrollText,
  ShieldCheck,
  Users,
  Landmark,
  ArrowRight,
  CheckCircle2,
  Crown,
  Briefcase,
  UserCheck,
} from 'lucide-react';

const Landing = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden tribal-pattern">
        <div className="absolute inset-0 cream-gradient opacity-80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-6">
                <Crown className="h-3.5 w-3.5" />
                Traditional Authority · Garo Hills District
              </div>
              <h1 className="font-serif text-5xl lg:text-6xl font-bold leading-[1.05] text-foreground">
                Nokma Sani <span className="text-bronze">Union</span>
              </h1>
              <p className="mt-3 text-xl font-serif text-accent italic">Ku’monganio Bil</p>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
                The official digital registry for traditional administration and local self-governance.
                Issue and verify land documents, trade NOCs and other certificates under the authority of the Nokma Sani.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 shadow-warm">
                    Register Now <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="border-primary/30 text-foreground hover:bg-primary/5">
                    Sign in to your account
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Authentic</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Transparent</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Community-led</div>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-6 rounded-full bg-gradient-to-br from-primary/20 via-accent/10 to-transparent blur-2xl" />
                <img
                  src={LOGO_URL}
                  alt="Nokma Sani Union Emblem"
                  className="relative h-72 w-72 lg:h-96 lg:w-96 rounded-full object-cover ring-4 ring-primary/40 shadow-warm"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-serif text-3xl lg:text-4xl font-bold">Four Pillars of the Union</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              Each role carries a sacred responsibility in the traditional self-governance of our A’khings.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, title: 'Administrator', desc: 'Approves Nokma Sani registrations and oversees Jakrikras.', tone: 'primary' },
              { icon: Briefcase, title: 'Jakrikra', desc: 'Office assistants who manage Mauzas and forward applications.', tone: 'accent' },
              { icon: Crown, title: 'Nokma Sani', desc: 'Traditional heads who approve and issue official documents.', tone: 'primary' },
              { icon: UserCheck, title: 'Applicant', desc: 'Citizens applying for NOCs, land or other documents.', tone: 'accent' },
            ].map((r) => (
              <Card key={r.title} className="border-border hover:shadow-warm transition-shadow duration-300">
                <CardContent className="pt-6">
                  <div className={`h-12 w-12 rounded-lg flex items-center justify-center mb-4 ${r.tone === 'primary' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'}`}>
                    <r.icon className="h-6 w-6" />
                  </div>
                  <div className="font-serif text-xl font-semibold mb-1">{r.title}</div>
                  <div className="text-sm text-muted-foreground leading-relaxed">{r.desc}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-serif text-3xl lg:text-4xl font-bold">Documents & Certificates</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              Apply for any of these documents through your local Nokma Sani.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {DOC_TYPES.map((d, i) => (
              <Card key={d.id} className="border-border bg-card hover:-translate-y-1 transition-transform duration-300 shadow-sm hover:shadow-warm">
                <CardContent className="pt-8 pb-8">
                  <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-5">
                    {i === 0 ? <ScrollText className="h-6 w-6" /> : i === 1 ? <Landmark className="h-6 w-6" /> : <Users className="h-6 w-6" />}
                  </div>
                  <h3 className="font-serif text-xl font-semibold">{d.title}</h3>
                  <div className="text-sm text-primary mt-1 mb-3">{d.subtitle}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{d.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-serif text-3xl lg:text-4xl font-bold">How the Process Flows</h2>
            <p className="mt-3 text-muted-foreground">From application to approval — transparent at every stage.</p>
          </div>
          <ol className="space-y-6">
            {[
              { n: '01', t: 'Register & Submit', d: 'Applicant registers, uploads identity documents, and submits an application choosing the type of document needed.' },
              { n: '02', t: 'Routed to Jakrikra', d: 'The application is automatically routed to the Jakrikra handling the relevant Mauza.' },
              { n: '03', t: 'Forwarded to Nokma Sani', d: 'The Jakrikra reviews and forwards the application to the Nokma Sani of the concerned A’khing.' },
              { n: '04', t: 'Approval & Issuance', d: 'The Nokma Sani approves and an official document is issued with a unique reference code.' },
            ].map((step) => (
              <li key={step.n} className="flex gap-5 p-5 rounded-xl bg-card border border-border hover:shadow-warm transition-shadow">
                <div className="font-serif text-3xl font-bold text-primary/50 min-w-[3rem]">{step.n}</div>
                <div>
                  <div className="font-serif text-xl font-semibold">{step.t}</div>
                  <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{step.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl p-10 lg:p-14 bg-card border border-border shadow-warm text-center">
            <h2 className="font-serif text-3xl lg:text-4xl font-bold">Begin your application today</h2>
            <p className="mt-3 text-muted-foreground">Join the official traditional registry of Garo Hills.</p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                  Create an account <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-primary/30">
                  I already have an account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
